import { COMBAT_VISUAL_LIMITS } from '../config/GameConfig';
import * as Phaser from 'phaser';
import PoolManager from '../managers/PoolManager';
import { EventBus } from '../core/EventBus';
import { PlayerStats } from '../components/PlayerStats';
import { createLogger } from '../utils/Logger';
import { AimVectorProvider, WeaponRuntimeConfig } from './WeaponTypes';

export interface IWeaponBehavior {
  dispose?(poolManager: PoolManager): void;
  fire(
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    level: number,
    stats?: PlayerStats,
    aimProvider?: AimVectorProvider
  ): void;

  update(
    dt: number,
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    level: number,
    stats?: PlayerStats,
    aimProvider?: AimVectorProvider
  ): void;
}

interface ActiveTimedProjectile {
  projectile: Phaser.Physics.Arcade.Sprite;
  ttl: number;
  generation: unknown;
}

interface ActiveAreaBurst extends ActiveTimedProjectile {
  radius: number;
  visual?: Phaser.GameObjects.Arc;
}

interface ActiveBoomerangProjectile extends ActiveTimedProjectile {
  returning: boolean;
}

interface ChainTarget {
  enemy: Phaser.Physics.Arcade.Sprite;
  damage: number;
  delayMs: number;
  fromX: number;
  fromY: number;
}

export class ProjectileAttack implements IWeaponBehavior {
  public dispose(poolManager: PoolManager): void {
    this.activeProjectiles.forEach(entry => { if (this.generations.get(entry) === entry.getData('visualGeneration')) poolManager.despawn(entry); });
    this.activeProjectiles = [];

  }

  private readonly logger = createLogger('ProjectileAttack');
  private activeProjectiles: Phaser.Physics.Arcade.Sprite[] = [];
  private generations = new WeakMap<Phaser.Physics.Arcade.Sprite, unknown>();

  public fire(
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    _level: number,
    stats?: PlayerStats,
    aimProvider?: AimVectorProvider
  ): void {
    const direction = getAimDirection(player, aimProvider);
    const damage = applyDamageMultiplier(config.damage.baseDamage + config.damage.bonusDamage, stats);
    const count = config.projectile.count || 1;

    for (let index = 0; index < count; index += 1) {
      const projectile = poolManager.spawnProjectile({
        x: player.x,
        y: player.y,
        type: config.sprite
      }) as Phaser.Physics.Arcade.Sprite | null;
      if (!projectile) break;
      if (!projectile.body) {
        this.logger.error('fire:projectile-missing-physics-body', { weaponId: config.id, poolType: config.sprite });
        poolManager.despawn(projectile);
        continue;
      }

      projectile.setData('damage', damage);
      projectile.setData('pierce', config.projectile.pierce || 1);
      projectile.setData('isOrbit', false);
      projectile.setData('hitList', []);
      attachDamageContext(projectile, config);
      launchProjectile(projectile, player, direction, index, count, config.projectile.spread, config.projectile.speed);


      this.activeProjectiles = this.activeProjectiles.filter(active => active !== projectile);
      this.generations.set(projectile, projectile.getData('visualGeneration'));
      this.activeProjectiles.push(projectile);
    }
  }

  public update(
    _dt: number,
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    _config: WeaponRuntimeConfig,
    _level: number,
    _stats?: PlayerStats,
    _aimProvider?: AimVectorProvider
  ): void {
    const camera = player.scene?.cameras.main;
    if (!camera) return;

    const view = camera.worldView;
    const margin = 50;

    this.activeProjectiles = this.activeProjectiles.filter(projectile => {
      if (!projectile.active || this.generations.get(projectile) !== projectile.getData('visualGeneration')) return false;

      const outOfBounds =
        projectile.x < view.x - margin ||
        projectile.x > view.x + view.width + margin ||
        projectile.y < view.y - margin ||
        projectile.y > view.y + view.height + margin;

      if (outOfBounds) {
        poolManager.despawn(projectile);
        return false;
      }

      return true;
    });
  }
}

export class OrbitAttack implements IWeaponBehavior {
  public dispose(poolManager: PoolManager): void {
    this.activeProjectiles.forEach(entry => { if (this.generations.get(entry) === entry.getData('visualGeneration')) poolManager.despawn(entry); });
    this.activeProjectiles = [];

  }

  private readonly logger = createLogger('OrbitAttack');
  private activeProjectiles: Phaser.Physics.Arcade.Sprite[] = [];
  private generations = new WeakMap<Phaser.Physics.Arcade.Sprite, unknown>();
  private angle = 0;

  public fire(
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    _level: number,
    stats?: PlayerStats
  ): void {
    this.activeProjectiles = this.activeProjectiles.filter(projectile => projectile.active && this.generations.get(projectile) === projectile.getData('visualGeneration'));
    const count = config.projectile.count || 1;
    if (this.activeProjectiles.length !== count) {
      this.activeProjectiles.forEach(projectile => {
        if (projectile.active) {
          poolManager.despawn(projectile);
        }
      });
      this.activeProjectiles = [];

      for (let index = 0; index < count; index += 1) {
        const projectile = poolManager.spawnProjectile({
          x: player.x,
          y: player.y,
          type: config.sprite
        }) as Phaser.Physics.Arcade.Sprite | null;

        if (projectile?.body) {
          this.activeProjectiles = this.activeProjectiles.filter(active => active !== projectile);
          this.generations.set(projectile, projectile.getData('visualGeneration'));
          this.activeProjectiles.push(projectile);
        } else if (projectile) {
          this.logger.error('fire:orbit-projectile-missing-physics-body', {
            weaponId: config.id,
            poolType: config.sprite
          });
        }
      }
    }

    const damage = applyDamageMultiplier(config.damage.baseDamage + config.damage.bonusDamage, stats);
    this.activeProjectiles.forEach(projectile => {
      if (!projectile.active) return;
      projectile.setData('damage', damage);
      projectile.setData('pierce', 999);
      projectile.setData('isOrbit', true);
      projectile.setData('hitList', []);
      attachDamageContext(projectile, config);
    });
  }

  public update(
    dt: number,
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    level: number,
    stats?: PlayerStats
  ): void {
    const count = config.projectile.count || 1;
    if (this.activeProjectiles.length !== count) {
      this.fire(poolManager, player, config, level, stats);
    }

    if (this.activeProjectiles.length === 0) return;

    const angularSpeed = Phaser.Math.DegToRad(config.projectile.speed);
    const radius = config.projectile.range ?? 60 + (level * 5);
    this.angle += angularSpeed * dt;

    const activeProjectiles = this.activeProjectiles.filter(projectile => projectile.active && this.generations.get(projectile) === projectile.getData('visualGeneration'));
    const angleStep = (Math.PI * 2) / activeProjectiles.length;

    activeProjectiles.forEach((projectile, index) => {
      const currentAngle = this.angle + (index * angleStep);
      const x = player.x + Math.cos(currentAngle) * radius;
      const y = player.y + Math.sin(currentAngle) * radius;
      projectile.setPosition(x, y);
    });

    this.activeProjectiles = activeProjectiles;
  }
}

export class AreaAttack implements IWeaponBehavior {
  public dispose(poolManager: PoolManager): void {
    this.activeBursts.forEach(entry => { entry.visual?.destroy(); if (entry.projectile.getData('visualGeneration') === entry.generation) poolManager.despawn(entry.projectile); });
    this.activeBursts = [];

  }

  private readonly logger = createLogger('AreaAttack');
  private activeBursts: ActiveAreaBurst[] = [];

  public fire(
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    _level: number,
    stats?: PlayerStats
  ): void {
    const count = config.projectile.count || 1;
    const duration = config.projectile.duration ?? config.projectile.life ?? 0.6;
    const damage = applyDamageMultiplier(config.damage.baseDamage + config.damage.bonusDamage, stats);
    const radius = Math.max(getHeroHitZoneRadius(player) * 4, config.projectile.area);

    for (let index = 0; index < count; index += 1) {
      const angle = (Math.PI * 2 * index) / count;
      const x = player.x + Math.cos(angle) * Math.max(0, radius * 0.05);
      const y = player.y + Math.sin(angle) * Math.max(0, radius * 0.05);
      const projectile = poolManager.spawnProjectile({
        x,
        y,
        type: 'weapon_area'
      }) as Phaser.Physics.Arcade.Sprite | null;

      if (!projectile?.body) {
        this.logger.error('fire:area-projectile-missing-physics-body', { weaponId: config.id, poolType: 'weapon_area' });
        continue;
      }

      configureAreaBody(projectile, radius);
      projectile.setVisible(false);
      projectile.setAlpha(0);
      projectile.setData('damage', damage);
      projectile.setData('pierce', 999);
      projectile.setData('isOrbit', true);
      projectile.setData('hitList', []);
      attachDamageContext(projectile, config);
      projectile.setData('areaRadius', radius);
      const visual = createAreaBoundary(player.scene, player.x, player.y, radius);
      this.activeBursts.push({ projectile, generation: projectile.getData('visualGeneration'), ttl: duration, radius, visual });
    }
  }

  public update(
    dt: number,
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    _config: WeaponRuntimeConfig,
    _level: number,
    _stats?: PlayerStats
  ): void {
    this.activeBursts = this.activeBursts.filter(entry => {
      if (!entry.projectile.active || entry.projectile.getData('visualGeneration') !== entry.generation) {
        entry.visual?.destroy();
        return false;
      }

      entry.ttl -= dt;
      if (entry.ttl > 0) {
        entry.projectile.setPosition(player.x, player.y);
        if (entry.projectile.body) {
          (entry.projectile.body as Phaser.Physics.Arcade.Body).reset(player.x, player.y);
        }
        entry.visual?.setPosition(player.x, player.y);
        entry.visual?.setFillStyle(0xff8844, 0.04 + 0.04 * Math.sin(entry.ttl * 15));
        configureAreaBody(entry.projectile, entry.radius);
        return true;
      }

      poolManager.despawn(entry.projectile);
      entry.visual?.destroy();
      return false;
    });
  }
}

export class BeamAttack implements IWeaponBehavior {
  public dispose(poolManager: PoolManager): void {
    this.activeBeams.forEach(entry => { if (entry.projectile.getData('visualGeneration') === entry.generation) poolManager.despawn(entry.projectile); });
    this.activeBeams = [];

  }

  private readonly logger = createLogger('BeamAttack');
  private activeBeams: ActiveTimedProjectile[] = [];

  public fire(
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    _level: number,
    stats?: PlayerStats
  ): void {
    const count = config.projectile.count || 1;
    const duration = config.projectile.duration ?? config.projectile.life ?? 0.45;
    const damage = applyDamageMultiplier(config.damage.baseDamage + config.damage.bonusDamage, stats);

    for (let index = 0; index < count; index += 1) {
      const projectile = poolManager.spawnProjectile({
        x: player.x,
        y: player.y,
        type: 'weapon_beam'
      }) as Phaser.Physics.Arcade.Sprite | null;

      if (!projectile?.body) {
        this.logger.error('fire:beam-projectile-missing-physics-body', { weaponId: config.id, poolType: 'weapon_beam' });
        continue;
      }

      projectile.setData('damage', damage);
      projectile.setData('pierce', 999);
      projectile.setData('isOrbit', true);
      projectile.setData('hitList', []);
      attachDamageContext(projectile, config);
      projectile.setDisplaySize(Math.max(16, (config.projectile.range ?? 160) * 0.5), 6);

      this.activeBeams.push({ projectile, generation: projectile.getData('visualGeneration'), ttl: duration });
    }
  }

  public update(
    dt: number,
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    _level: number,
    _stats?: PlayerStats,
    aimProvider?: AimVectorProvider
  ): void {
    const direction = getAimDirection(player, aimProvider);
    const range = config.projectile.range ?? 160;

    this.activeBeams = this.activeBeams.filter((entry, index) => {
      const projectile = entry.projectile;
      if (!projectile.active || projectile.getData('visualGeneration') !== entry.generation) return false;

      entry.ttl -= dt;
      const beamOffset = range * (0.4 + index * 0.1);
      projectile.setPosition(player.x + direction.x * beamOffset, player.y + direction.y * beamOffset);
      projectile.setRotation(Math.atan2(direction.y, direction.x));
      const body = projectile.body as Phaser.Physics.Arcade.Body;
      const width = Math.abs(direction.x) * projectile.displayWidth + Math.abs(direction.y) * projectile.displayHeight;
      const height = Math.abs(direction.y) * projectile.displayWidth + Math.abs(direction.x) * projectile.displayHeight;
      body.setSize(width / Math.abs(projectile.scaleX), height / Math.abs(projectile.scaleY), true);
      body.reset(projectile.x, projectile.y);

      if (entry.ttl > 0) return true;

      poolManager.despawn(projectile);
      return false;
    });
  }
}

export class BoomerangAttack implements IWeaponBehavior {
  public dispose(poolManager: PoolManager): void {
    this.activeBoomerangs.forEach(entry => { if (entry.projectile.getData('visualGeneration') === entry.generation) poolManager.despawn(entry.projectile); });
    this.activeBoomerangs = [];

  }

  private readonly logger = createLogger('BoomerangAttack');
  private activeBoomerangs: ActiveBoomerangProjectile[] = [];

  public fire(
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    _level: number,
    stats?: PlayerStats,
    aimProvider?: AimVectorProvider
  ): void {
    const count = config.projectile.count || 1;
    const direction = getAimDirection(player, aimProvider);
    const speed = config.projectile.speed || 220;
    const ttl = config.projectile.life || 1.2;
    const damage = applyDamageMultiplier(config.damage.baseDamage + config.damage.bonusDamage, stats);

    for (let index = 0; index < count; index += 1) {
      const projectile = poolManager.spawnProjectile({
        x: player.x,
        y: player.y,
        type: 'weapon_boomerang'
      }) as Phaser.Physics.Arcade.Sprite | null;
      if (!projectile) break;
      if (!projectile.body) {
        this.logger.error('fire:boomerang-spawn-failed', { weaponId: config.id, poolType: 'weapon_boomerang' });
        poolManager.despawn(projectile);
        continue;
      }

      projectile.setData('damage', damage);
      projectile.setData('pierce', config.projectile.pierce || 2);
      projectile.setData('isOrbit', false);
      projectile.setData('hitList', []);
      attachDamageContext(projectile, config);
      launchProjectile(projectile, player, direction, index, count, config.projectile.spread, speed);

      const previousCount = this.activeBoomerangs.length;
      this.activeBoomerangs = this.activeBoomerangs.filter(entry => entry.projectile !== projectile);
      if (this.activeBoomerangs.length < previousCount) {
      }
      this.activeBoomerangs.push({ projectile, generation: projectile.getData('visualGeneration'), ttl, returning: false });
    }
  }

  public update(
    dt: number,
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    _level: number,
    _stats?: PlayerStats
  ): void {
    const returnSpeed = config.projectile.returnSpeed ?? Math.max(120, config.projectile.speed);

    this.activeBoomerangs = this.activeBoomerangs.filter(entry => {
      const { projectile } = entry;
      if (!projectile.active || !projectile.body || projectile.getData('visualGeneration') !== entry.generation) return false;

      entry.ttl -= dt;
      const halfLife = (config.projectile.life || 1.2) * 0.5;
      if (!entry.returning && entry.ttl <= halfLife) {
        entry.returning = true;
        projectile.setData('visualReturning', true);
      }

      if (entry.returning) {
        const direction = new Phaser.Math.Vector2(player.x - projectile.x, player.y - projectile.y).normalize();
        (projectile.body as Phaser.Physics.Arcade.Body).setVelocity(direction.x * returnSpeed, direction.y * returnSpeed);
      }

      const reachedPlayer =
        entry.returning && Phaser.Math.Distance.Between(projectile.x, projectile.y, player.x, player.y) < 14;
      if (!reachedPlayer && entry.ttl > 0) {
        return true;
      }

      poolManager.despawn(projectile);
      return false;
    });
  }
}

export class ChainAttack implements IWeaponBehavior {
  public dispose(poolManager: PoolManager): void {
    this.activeChains.forEach(entry => { if (entry.projectile.getData('visualGeneration') === entry.generation) poolManager.despawn(entry.projectile); });
    this.activeChains = [];
    this.pending = [];
  }

  private readonly logger = createLogger('ChainAttack');
  private activeChains: ActiveTimedProjectile[] = [];
  private pending: Array<{ ttl: number; action: () => void }> = [];

  public fire(
    poolManager: PoolManager,
    player: Phaser.Physics.Arcade.Sprite,
    config: WeaponRuntimeConfig,
    _level: number,
    stats?: PlayerStats
  ): void {
    const damage = applyDamageMultiplier(config.damage.baseDamage + config.damage.bonusDamage, stats);
    const chainHit = config.damage.chainHit || config.projectile.chainHit || 1;
    const maxTargets = config.damage.chainLightning?.maxTargets ?? chainHit;
    const radius = config.damage.chainLightning?.radius ?? config.projectile.range ?? 140;
    const targets = selectChainTargets(player, radius, maxTargets);

    if (targets.length === 0) {
      return;
    }

    const chainTargets = buildChainTargetSequence(player, targets, damage, config.damage.chainLightning?.falloff ?? 0);

    chainTargets.forEach(target => {
      const generation = target.enemy.getData('visualGeneration');
      const action = () => {
        if (target.enemy.getData('visualGeneration') !== generation) return;
        if (!target.enemy.active) return;


        const projectile = poolManager.spawnProjectile({
          x: target.enemy.x,
          y: target.enemy.y,
          type: 'weapon_chain'
        }) as Phaser.Physics.Arcade.Sprite | null;

        if (!projectile || !projectile.body) {
          this.logger.error('fire:chain-spawn-failed', { weaponId: config.id, poolType: 'weapon_chain' });
          return;
        }

        projectile.setVisible(false);
        projectile.setAlpha(0);
        projectile.setData('damage', target.damage);
        projectile.setData('pierce', 999);
        projectile.setData('chainTarget', target.enemy);
        projectile.setData('chainFrom', { x: target.fromX, y: target.fromY });
        projectile.setData('chainHit', 1);
        projectile.setData('chainLightning', undefined);
        projectile.setData('chainMaxTargets', 1);
        projectile.setData('isOrbit', true);
        projectile.setData('hitList', []);
        attachDamageContext(projectile, {
          ...config,
          damage: {
            ...config.damage,
            baseDamage: target.damage,
            chainLightning: undefined,
            chainHit: 0
          }
        });
        (projectile.body as Phaser.Physics.Arcade.Body).reset(target.enemy.x, target.enemy.y);
        (projectile.body as Phaser.Physics.Arcade.Body).setSize(20, 20, true);

        this.activeChains.push({ projectile, generation: projectile.getData('visualGeneration'), ttl: 0.12 });
      };
      if (target.delayMs === 0 || this.pending.length < COMBAT_VISUAL_LIMITS.pendingChainHits) {
        this.pending.push({ ttl: target.delayMs / 1000, action });
      }
    });
  }

  public update(
    dt: number,
    poolManager: PoolManager,
    _player: Phaser.Physics.Arcade.Sprite,
    _config: WeaponRuntimeConfig,
    _level: number,
    _stats?: PlayerStats
  ): void {
    this.activeChains = this.activeChains.filter(entry => {
      if (!entry.projectile.active || entry.projectile.getData('visualGeneration') !== entry.generation) return false;

      entry.ttl -= dt;
      if (entry.ttl > 0) return true;

      poolManager.despawn(entry.projectile);
      return false;
    });
    // Новые удары должны дожить до следующей проверки столкновений даже при длинном кадре.
    this.pending = this.pending.filter(entry => { entry.ttl -= dt; if (entry.ttl > 0) return true; entry.action(); return false; });
  }
}

function getHeroHitZoneRadius(player: Phaser.Physics.Arcade.Sprite): number {
  const body = player.body as Phaser.Physics.Arcade.Body | undefined;
  if (body) {
    return Math.max(body.width, body.height) / 2;
  }

  return Math.max(player.displayWidth || player.width || 32, player.displayHeight || player.height || 32) / 2;
}

function configureAreaBody(projectile: Phaser.Physics.Arcade.Sprite, radius: number): void {
  const diameter = radius * 2;
  projectile.setDisplaySize(diameter, diameter);
  const body = projectile.body as Phaser.Physics.Arcade.Body | undefined;
  if (!body) return;

  body.setCircle(projectile.width / 2, 0, 0);
}

function createAreaBoundary(
  scene: Phaser.Scene | undefined,
  x: number,
  y: number,
  radius: number
): Phaser.GameObjects.Arc | undefined {
  if (!scene?.add) return undefined;

  const boundary = scene.add.circle(x, y, radius);
  boundary.setFillStyle();
  boundary.setStrokeStyle(3, 0xff8844, 0.9);
  boundary.setDepth(12);
  return boundary;
}

function selectChainTargets(
  player: Phaser.Physics.Arcade.Sprite,
  radius: number,
  maxTargets: number
): Phaser.Physics.Arcade.Sprite[] {
  const activeEnemies = getDynamicBodyGameObjects(player.scene.physics.world)
    .map(body => (body as { gameObject?: Phaser.Physics.Arcade.Sprite }).gameObject)
    .filter((candidate): candidate is Phaser.Physics.Arcade.Sprite => {
      if (!candidate || candidate === player || !candidate.active) return false;
      return candidate.getData('health') !== undefined && candidate.getData('id') !== undefined;
    });

  const selected: Phaser.Physics.Arcade.Sprite[] = [];
  let originX = player.x;
  let originY = player.y;

  while (selected.length < maxTargets) {
    const next = activeEnemies
      .filter(enemy => !selected.includes(enemy))
      .map(enemy => ({
        enemy,
        distance: Phaser.Math.Distance.Between(originX, originY, enemy.x, enemy.y)
      }))
      .filter(entry => entry.distance <= radius)
      .sort((left, right) => left.distance - right.distance)[0]?.enemy;

    if (!next) break;
    selected.push(next);
    originX = next.x;
    originY = next.y;
  }

  return selected;
}

function getDynamicBodyGameObjects(world: Phaser.Physics.Arcade.World): unknown[] {
  const bodies = (world as unknown as { bodies?: unknown }).bodies;

  if (bodies instanceof Set) {
    return Array.from(bodies);
  }

  if (Array.isArray(bodies)) {
    return bodies;
  }

  const maybeEntries = (bodies as { entries?: unknown } | undefined)?.entries;
  if (Array.isArray(maybeEntries)) {
    return maybeEntries;
  }

  if (typeof maybeEntries === 'function') {
    return Array.from(maybeEntries.call(bodies));
  }

  return [];
}

function buildChainTargetSequence(
  player: Phaser.Physics.Arcade.Sprite,
  targets: Phaser.Physics.Arcade.Sprite[],
  baseDamage: number,
  falloff: number
): ChainTarget[] {
  let fromX = player.x;
  let fromY = player.y;

  return targets.map((enemy, index) => {
    const target: ChainTarget = {
      enemy,
      damage: Math.max(1, baseDamage * (1 - (falloff * index))),
      delayMs: index * 90,
      fromX,
      fromY
    };
    fromX = enemy.x;
    fromY = enemy.y;
    return target;
  });
}

function getAimDirection(player: Phaser.Physics.Arcade.Sprite, aimProvider?: AimVectorProvider): Phaser.Math.Vector2 {
  const providedVector = aimProvider?.getAimVector();
  if (providedVector && (Math.abs(providedVector.x) > 0.001 || Math.abs(providedVector.y) > 0.001)) {
    return new Phaser.Math.Vector2(providedVector.x, providedVector.y).normalize();
  }

  const pointer = player.scene?.input.activePointer;
  if (!pointer) {
    return new Phaser.Math.Vector2(0, -1);
  }

  const angle = Phaser.Math.Angle.Between(player.x, player.y, pointer.worldX, pointer.worldY);
  return new Phaser.Math.Vector2(Math.cos(angle), Math.sin(angle));
}

function launchProjectile(
  projectile: Phaser.Physics.Arcade.Sprite,
  player: Phaser.Physics.Arcade.Sprite,
  direction: Phaser.Math.Vector2,
  index: number,
  count: number,
  spread: number,
  speed: number
): void {
  const centeredIndex = index - (count - 1) / 2;
  const angle = Math.atan2(direction.y, direction.x) + centeredIndex * spread;
  const offset = spread === 0
    ? centeredIndex * Math.max(projectile.displayWidth, projectile.displayHeight)
    : 0;
  const body = projectile.body as Phaser.Physics.Arcade.Body;
  body.reset(player.x - direction.y * offset, player.y + direction.x * offset);
  body.setVelocity(Math.cos(angle) * speed, Math.sin(angle) * speed);
}

function applyDamageMultiplier(baseDamage: number, stats?: PlayerStats): number {
  const multiplier = stats ? stats.get('damageMultiplier') : 1;
  return baseDamage * multiplier;
}

function publishWeaponFired(weaponId: string, projectile: Phaser.Physics.Arcade.Sprite, damage: number): void {
  EventBus.getInstance().publish({
    type: 'weapon:fired',
    data: {
      weaponId,
      projectile,
      damage
    }
  });
}

function attachDamageContext(projectile: Phaser.Physics.Arcade.Sprite, config: WeaponRuntimeConfig): void {
  projectile.setData('weaponType', config.type);
  projectile.setData('weaponId', config.id);
  projectile.setData('visualReturning', false);
  publishWeaponFired(config.id, projectile, Number(projectile.getData('damage') ?? 0));
  projectile.setData('damagePayload', config.damage);
  projectile.setData('areaRadius', config.projectile.area);
}
